import { IconLayer } from '@deck.gl/layers';
import type { Device } from '@luma.gl/core';
import { memoize } from 'lodash';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import type { FlagContent, StarterType } from 'src/lib/player/store';
import { hasMouse } from 'src/lib/player/utils';
import SpriteManager, { type Sprite, type SpriteSheet } from './SpriteManager';

const getStarterMarkerSpriteManager = memoize(() => new SpriteManager());

type StarterMarkerLayerProps = {
  renderInfoBuffer: RenderInfoBuffer;
  starters: Array<StarterType>;
  selectedIds: Set<string>;
  hiddenIds: Set<string>;
  flagContentByEventIndex: Array<FlagContent>;
  overrideFlagContent: null | FlagContent;
  selectedFlagContentByEventIndex: Array<FlagContent>;
  overrideSelectedFlagContent: null | FlagContent;
  largeMode: boolean;
  onClick: (starter: StarterType) => void;
  onHover: (starter: StarterType | null) => void;
  gpuDevice: Device | null;
};

export default function createStarterMarkerLayer({
  renderInfoBuffer,
  starters,
  selectedIds,
  hiddenIds,
  flagContentByEventIndex,
  overrideFlagContent,
  selectedFlagContentByEventIndex,
  overrideSelectedFlagContent,
  largeMode,
  onClick,
  onHover,
  gpuDevice,
}: StarterMarkerLayerProps): Array<IconLayer> {
  if (gpuDevice == null) return [];

  const spriteManager = getStarterMarkerSpriteManager();
  const starterSprites: Array<Sprite> = [];
  for (let i = 0; i < starters.length; i++) {
    const isSelected = selectedIds.has(renderInfoBuffer.starters[i]?.id);
    if (
      (renderInfoBuffer.getShowMarker(i) || isSelected) &&
      !(renderInfoBuffer.getLng(i) === 0 && renderInfoBuffer.getLat(i) === 0)
    ) {
      const starter = starters[i];
      starterSprites.push(
        spriteManager.createStarterSprite(
          starter,
          renderInfoBuffer.getLat(i),
          renderInfoBuffer.getLng(i),
          renderInfoBuffer.getOpacity(i),
          isSelected
            ? (overrideSelectedFlagContent ?? selectedFlagContentByEventIndex[starter.eventIndex])
            : (overrideFlagContent ?? flagContentByEventIndex[starter.eventIndex]),
          largeMode,
        ),
      );
    }
  }

  function makeIconLayer(isSelected: boolean, spritesheet: SpriteSheet, i: number) {
    return new IconLayer({
      id: `startermarker-icons-${isSelected}-${i}`,
      iconMapping: spriteManager.sprites,
      iconAtlas: spritesheet.texture || undefined,
      data: starterSprites.filter((sprite) => {
        const id = sprite._starter?.id ?? sprite._poi?.id ?? '';
        return !hiddenIds.has(id) && !isSelected !== selectedIds.has(id) && sprite.sheetIndex === i;
      }),
      billboard: true,
      pickable: true,
      getSize: (sprite: Sprite) => ((isSelected ? 1 : 0.65) * sprite.height) / DEVICE_PIXEL_RATIO,
      getIcon: (sprite: Sprite) => sprite.hashKey,
      // @ts-expect-error: ts(2322) Return type of getPosition is [number, number, number] but deck.gl expects [number, number]
      getPosition: (sprite: Sprite, { target }: { target: [number, number, number] }) => {
        target[0] = sprite.lng;
        target[1] = sprite.lat;
        target[2] = 2.1;
        return target;
      },
      // @ts-expect-error: ts(2322) Return type of getColor is [number, number, number, number] but deck.gl expects [number, number]
      getColor: (sprite: Sprite, { target }: { target: [number, number, number, number] }) => {
        target[0] = 255;
        target[1] = 255;
        target[2] = 255;
        target[3] = sprite.alpha;
        return target;
      },
      onClick: ({ object }: { object?: Sprite | null }) => {
        if (object?._starter != null) {
          onClick(object._starter);
        }
      },
      onHover: hasMouse()
        ? ({ object }: { object?: Sprite | null }) => {
            onHover(object?._starter ?? null);
          }
        : null,
    });
  }

  spriteManager.updateDirtySpritesheets(gpuDevice);
  return [
    ...spriteManager.spritesheets.map((spritesheet, i) => makeIconLayer(false, spritesheet, i)),
    ...spriteManager.spritesheets.map((spritesheet, i) => makeIconLayer(true, spritesheet, i)),
  ];
}
